import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { isEqual } from 'lodash'
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'
import { useTranslation } from 'react-i18next'

import ModalContainer from '../../../ModalContainer/ModelContainer'
import ToggleField from '../../../FormLegacy/FormFieldsV2/ToggleField'
import Icon from '../../../Icons'
import { selectLoyaltySettings, updateLoyaltySettings } from '../../../../reducers/loyaltyReducer'
import { getNamedHandler } from '../helpers'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'

const RewardsLoyaltySettingsModal = ({
  onClose = () => {},
  updateLoyaltySettings = () => {},
  selectedBuilding,
  settings,
}) => {
  const { t } = useTranslation(['rewards-loyalty'])
  const {
    isPointsAccumulationEnabled,
    isLoyaltySystemEnabled,
    rewardRedemptionApprovalNeeded,
    health,
  } = settings
  const currState = {
    isPointsAccumulationPaused: !isPointsAccumulationEnabled,
    isLoyaltySystemPaused: !isLoyaltySystemEnabled,
    rewardRedemptionApprovalNeeded,
  }

  const [formState, setFormState] = useState(currState)
  const { displaySnackbar } = useSnackbar()

  const updateSettings = async () => {
    try {
      const requestData = {
        isPointsAccumulationEnabled: !formState.isPointsAccumulationPaused,
        isLoyaltySystemEnabled: !formState.isLoyaltySystemPaused,
        rewardRedemptionApprovalNeeded: formState.rewardRedemptionApprovalNeeded,
      }

      await updateLoyaltySettings(selectedBuilding.id, requestData)
    } catch ({ message }) {
      displaySnackbar(message, true)
    }
  }

  const handleFieldChange = ({ target: { name, value } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const onHandleNameField = getNamedHandler(handleFieldChange)

  useEffect(() => {
    if (selectedBuilding && !isEqual(currState, formState)) {
      updateSettings()
    }
  }, [formState, currState, selectedBuilding])

  return (
    <ModalContainer
      additionalClass={'rewards-modal'}
      onClose={onClose}
      headerTitle={t('rewards-loyalty:loyaltySettings.headerTitle')}
      headerIcon={<Icon icon="diamondStar" />}
    >
      <div className="rewards-modal-content rewards-base-view loyalty-settings">
        <div className="rewards-modal-input-row">
          <div className="input-container">
            <ToggleField
              label={
                <React.Fragment>
                  {t('rewards-loyalty:loyaltySettings.pausePointsAccumulationLabel')}
                  <div data-tip data-for="pause-points-tip">
                    <Icon icon="info" viewBox="0 0 25 25" />
                    <ReactTooltip className="tooltip" id="pause-points-tip" multiline place="top">
                      {t('rewards-loyalty:loyaltySettings.pausePointsAccumulationTooltip')}
                    </ReactTooltip>
                  </div>
                </React.Fragment>
              }
              labelOnRight
              name="isPointsAccumulationPaused"
              onChange={() =>
                onHandleNameField(
                  !formState.isPointsAccumulationPaused,
                  'isPointsAccumulationPaused',
                )
              }
              value={formState.isPointsAccumulationPaused}
            />
          </div>
        </div>
        <div className="rewards-modal-input-row">
          <div className="input-container">
            <ToggleField
              label={
                <React.Fragment>
                  {t('rewards-loyalty:loyaltySettings.pauseLoyaltySystemLabel')}
                  <div data-tip data-for="pause-loyalty-tip">
                    <Icon icon="info" viewBox="0 0 25 25" />
                    <ReactTooltip className="tooltip" id="pause-loyalty-tip" multiline place="top">
                      {t('rewards-loyalty:loyaltySettings.pauseLoyaltySystemTooltip')}
                    </ReactTooltip>
                  </div>
                </React.Fragment>
              }
              labelOnRight
              name="isLoyaltySystemPaused"
              onChange={() =>
                onHandleNameField(!formState.isLoyaltySystemPaused, 'isLoyaltySystemPaused')
              }
              value={formState.isLoyaltySystemPaused}
            />
          </div>
        </div>
        <div className="rewards-modal-input-row">
          <div className="input-container">
            <ToggleField
              label={
                <React.Fragment>
                  {t('rewards-loyalty:loyaltySettings.manualRewardApprovalLabel')}
                  <div data-tip data-for="manual-reward-tip">
                    <Icon icon="info" viewBox="0 0 25 25" />
                    <ReactTooltip className="tooltip" id="manual-reward-tip" multiline place="top">
                      {t('rewards-loyalty:loyaltySettings.manualRewardApprovalTooltip')}
                    </ReactTooltip>
                  </div>
                </React.Fragment>
              }
              labelOnRight
              name="rewardRedemptionApprovalNeeded"
              onChange={() =>
                onHandleNameField(
                  !formState.rewardRedemptionApprovalNeeded,
                  'rewardRedemptionApprovalNeeded',
                )
              }
              value={formState.rewardRedemptionApprovalNeeded}
            />
          </div>
        </div>
        <div className="rewards-modal-input-row">
          <div className="input-container">
            <div className="input-label">
              {t('rewards-loyalty:loyaltySettings.potentialRewardConfiguredLabel')}
            </div>
            <div className="reward-container">
              <div className="reward-container__item">
                <span>{t('rewards-loyalty:loyaltySettings.totalValueOfActivitiesLabel')}</span>
                <span>{`$${health.totalActivitiesValue}`}</span>
              </div>
              <div className="reward-container__item">
                <span className="sign">✖</span>
              </div>
              <div className="reward-container__item">
                <span>{t('rewards-loyalty:loyaltySettings.numberOfResidentsLabel')}</span>
                <span>{health.totalResidents}</span>
              </div>
              <div className="reward-container__item">
                <span className="sign">=</span>
              </div>
              <div className="reward-container__item">
                <span>{t('rewards-loyalty:loyaltySettings.potentialRewardValueLabel')}</span>
                <span>{`$${health.potentialRewardValue}`}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="rewards-modal-input-row">
          <div className="input-container">
            <span className="alert-container">
              <Icon icon="exclamationPointTriangle" />
              <strong>{`$${health.unredeemedPointsValue}`}</strong>
              {t('rewards-loyalty:loyaltySettings.valueOfUnredeemedPointsLabel')}
            </span>
          </div>
        </div>
      </div>
    </ModalContainer>
  )
}

RewardsLoyaltySettingsModal.propTypes = {
  onClose: PropTypes.func,
  updateLoyaltySettings: PropTypes.func,
  selectedBuilding: PropTypes.object,
  settings: PropTypes.object,
}

export default connect((state) => ({ settings: selectLoyaltySettings(state) }), {
  updateLoyaltySettings,
})(RewardsLoyaltySettingsModal)
